import { useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createVacationDecisionSchema } from '@/features/Vacations/models/vacation.schemas';
import type { VacationDecisionFormData } from '@/features/Vacations/models/vacation.schemas';
import { formatDayOffDates } from '@/features/DpRequests/utils/formatDayOffDates';
import { CANCEL_LABEL, DECISION_COPY } from '@/features/Vacations/components/VacationDecisionModal/vacationDecisionModal.definitions';
import { DAY_OFF_CARD_LABELS, DAY_OFF_DECISION_COPY } from '../approvals.definitions';
import type { ApprovalDecisionKind, ApprovalDecisionSubmission, DayOffApproval } from '../approvals.types';

interface DayOffDecisionModalProps {
    isOpen: boolean;
    approval: DayOffApproval | null;
    kind: ApprovalDecisionKind;
    onClose: () => void;
    onConfirm: (approval: DayOffApproval, decision: ApprovalDecisionSubmission) => void;
    isSubmitting?: boolean;
}

export function DayOffDecisionModal({
    isOpen,
    approval,
    kind,
    onClose,
    onConfirm,
    isSubmitting = false,
}: DayOffDecisionModalProps) {
    const copy = DAY_OFF_DECISION_COPY[kind];
    const confirmCopy = DECISION_COPY[kind];

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<VacationDecisionFormData>({
        resolver: zodResolver(createVacationDecisionSchema(kind)),
        defaultValues: { note: '' },
    });

    useEffect(() => {
        if (isOpen) reset({ note: '' });
    }, [isOpen, approval?.id, kind, reset]);

    if (!isOpen || !approval) return null;

    const submit = handleSubmit((data) => {
        onConfirm(approval, { note: data.note?.trim() ?? '' });
    });

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
                    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                        <div className="border-b border-gray-200 px-6 py-5">
                            <Dialog.Title className="text-base font-semibold text-raro-gray-900 font-montserrat">
                                {copy.title}
                            </Dialog.Title>
                            <p className="mt-1 text-sm text-raro-gray-600 font-montserrat whitespace-pre-line">
                                {copy.description}
                            </p>
                        </div>

                        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
                            <div className="rounded-xl bg-raro-surface-50 p-3">
                                <p className="text-sm font-semibold text-raro-gray-900 font-montserrat">
                                    {approval.collaboratorName}
                                </p>
                                <p className="text-xs text-raro-gray-600 font-montserrat">
                                    {DAY_OFF_CARD_LABELS.title}
                                </p>
                                <p className="mt-1 text-xs text-raro-gray-700 font-montserrat">
                                    {formatDayOffDates(approval.request.days).join(' · ')}
                                </p>
                                {approval.request.reason && (
                                    <p className="mt-1 text-xs text-raro-gray-500 font-montserrat">
                                        {DAY_OFF_CARD_LABELS.reason}: {approval.request.reason}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="day-off-decision-note"
                                    className="block text-sm font-medium text-raro-gray-700 font-montserrat"
                                >
                                    {copy.noteLabel}
                                </label>
                                <textarea
                                    id="day-off-decision-note"
                                    rows={3}
                                    autoFocus
                                    maxLength={500}
                                    placeholder={copy.notePlaceholder}
                                    aria-invalid={Boolean(errors.note)}
                                    {...register('note')}
                                    className="mt-1 block w-full rounded-xl border border-primary-200 p-2.5 text-sm text-raro-gray-900 focus:border-primary-500 focus:ring-primary-500 font-montserrat"
                                />
                                {errors.note && (
                                    <p className="mt-1 text-xs text-red-600 font-montserrat">{errors.note.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-raro-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 font-montserrat"
                            >
                                {CANCEL_LABEL}
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={clsx(
                                    'rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 font-montserrat',
                                    confirmCopy.confirmClassName
                                )}
                            >
                                {isSubmitting ? 'Salvando...' : confirmCopy.confirmLabel}
                            </button>
                        </div>
                    </form>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
